import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion } from 'framer-motion';
import {
  PlusCircle,
  Filter as FilterIcon,
  Users,
  Building2,
  ShieldAlert,
} from 'lucide-react';
import {
  Card,
  CardHeader,
  CardTitle,
  CardDescription,
  CardContent,
} from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import DataTable from '../../components/DataTable';
import Pagination from '../../components/Pagination';
import DynamicFormModal from '../../components/DynamicFormModal';
import FilterDrawer from '../../components/FilterDrawer';
import AppliedFilters from '../../components/AppliedFilters';
import ExportExcelButton from '../../components/ExportExcelButton';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { useToast } from '../../components/ui/toaster.jsx';
import { useAuth } from '../../contexts/AuthContext.jsx';
import { fetchTenants } from '../tenants/Services/tenantsService.js';
import {
  fetchUsers,
  createUser,
  updateUser,
  deleteUser,
  exportUsers,
} from './Services/usersService.js';
import { getUsersColumns, columnsExcel } from './utils/usersColumns.jsx';
import { getRoleLabel, getRoleBadgeClass } from '../../lib/roles';

const ITEMS_PER_PAGE = 10;
const DEFAULT_FILTERS = { estado: 'Activo' };

const MONGO_ID_PATTERN = /^[a-fA-F0-9]{24}$/;

export default function UsersPage() {
  const toast = useToast();
  const { tenant, isPlatformMode } = useAuth();

  // --- Estado principal ---
  const [displayedData, setDisplayedData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [filters, setFilters] = useState(DEFAULT_FILTERS);

  // --- Modales / drawers ---
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentItem, setCurrentItem] = useState(null);
  const [modalMode, setModalMode] = useState('create'); // 'create' | 'edit'
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);

  // Opciones de tenant para el form (solo en plataforma)
  const [tenantOptions, setTenantOptions] = useState([]);

  // --- Datos: cargar usuarios ---
  const fetchAndSetUsers = useCallback(async () => {
    setIsLoading(true);
    try {
      const r = await fetchUsers({
        limit: itemsPerPage,
        offset: currentPage,
        query: filters,
      });
      setDisplayedData(r?.data || []);
      setTotalItems(r?.totalRecords || 0);
    } catch (e) {
      toast.error('Error al cargar usuarios', e?.message || 'Intenta nuevamente');
    } finally {
      setIsLoading(false);
    }
  }, [currentPage, itemsPerPage, filters, toast]);

  useEffect(() => {
    fetchAndSetUsers();
  }, [fetchAndSetUsers]);

  // --- Cargar tenants cuando estamos en plataforma (para el form y el filtro) ---
  useEffect(() => {
    if (!isPlatformMode) return;
    (async () => {
      try {
        const r = await fetchTenants({ limit: 200, offset: 1, query: {} });
        const opts = (r?.data || []).map((t) => ({
          value: t._id,
          label: `${t.nombre} (${t.slug})`,
        }));
        setTenantOptions(opts);
      } catch {
        setTenantOptions([]);
      }
    })();
  }, [isPlatformMode]);

  // --- Definición de filtros (Drawer) ---
  const filterFields = useMemo(() => {
    const base = [
      {
        id: 'estado',
        label: 'Estado',
        type: 'select',
        options: [
          { value: 'Activo', label: 'Activo' },
          { value: 'Inactivo', label: 'Inactivo' },
        ],
      },
      {
        id: 'rol',
        label: 'Rol',
        type: 'select',
        options: [
          { value: 'admin', label: 'Administrador' },
          { value: 'comercial', label: 'Comercial' },
          { value: 'operaciones', label: 'Operaciones' },
          { value: 'facturacion', label: 'Facturación' },
          { value: 'cliente', label: 'Cliente' },
        ],
      },
      {
        id: 'nombre',
        label: 'Nombre contiene',
        type: 'text',
      },
      {
        id: 'correo',
        label: 'Correo contiene',
        type: 'text',
      },
    ];
    if (isPlatformMode) {
      base.push({
        id: 'tenantId',
        label: 'Tenant',
        type: 'select',
        options: tenantOptions,
      });
    }
    return base;
  }, [isPlatformMode, tenantOptions]);

  // --- Definición de columnas para el form (DynamicFormModal) ---
  const formFields = useMemo(
    () => getUsersColumns({ tenant, tenantOptions }),
    [tenant, tenantOptions],
  );

  // --- Columnas de la DataTable ---
  const columns = useMemo(
    () => [
      {
        id: 'estado',
        label: 'Estado',
        type: 'status',
        width: '110px',
      },
      { id: 'nombre', label: 'Nombre' },
      { id: 'correo', label: 'Correo' },
      { id: 'cedula', label: 'Cédula / NIT' },
      {
        id: 'rol',
        label: 'Rol',
        render: (row) => (
          <span
            className={`text-xs px-2 py-0.5 rounded-full font-medium ${getRoleBadgeClass(
              row.rol,
            )}`}
          >
            {getRoleLabel(row.rol)}
          </span>
        ),
      },
      ...(isPlatformMode
        ? [
            {
              id: 'tenantId',
              label: 'Tenant',
              render: (row) =>
                row?.tenantId ? (
                  <code className="text-xs text-muted-foreground">{row.tenantId}</code>
                ) : (
                  <span className="text-muted-foreground text-xs">—</span>
                ),
            },
          ]
        : []),
    ],
    [isPlatformMode],
  );

  // --- Handlers ---
  const handlePageChange = (page) => setCurrentPage(page);
  const handleLimitChange = (n) => {
    setItemsPerPage(n);
    setCurrentPage(1);
  };

  const handleOpenModal = (mode, item = null) => {
    setModalMode(mode);
    setCurrentItem(item);
    setIsModalOpen(true);
  };
  const handleCloseModal = () => {
    setIsModalOpen(false);
    setCurrentItem(null);
  };

  const handleSaveItem = async (formData) => {
    try {
      // En creación: validar contraseñas coincidentes y longitud mínima
      if (modalMode === 'create') {
        if (formData.contrasena !== formData.contrasena2) {
          toast.error('Las contraseñas no coinciden');
          return;
        }
        if ((formData.contrasena || '').length < 6) {
          toast.error('La contraseña debe tener al menos 6 caracteres');
          return;
        }
        const { contrasena2, ...payload } = formData;
        // En modo tenant, inyectar tenantId del contexto
        if (tenant?._id) {
          payload.tenantId = String(tenant._id);
        }
        // En plataforma, validar que el tenantId es ObjectId
        if (isPlatformMode) {
          if (!payload.tenantId || !MONGO_ID_PATTERN.test(payload.tenantId)) {
            toast.error('Selecciona un tenant válido');
            return;
          }
        }
        const r = await createUser(payload);
        if (r?.success) {
          toast.success('Usuario creado', `${payload.nombre} (${payload.correo})`);
        } else {
          toast.error('Error al crear usuario', r?.message);
          return;
        }
      } else if (modalMode === 'edit') {
        const clean = { ...formData };
        // Nunca dejar cambiar el tenantId en edición
        if (tenant?._id) clean.tenantId = String(tenant._id);
        if (isPlatformMode && clean.tenantId && !MONGO_ID_PATTERN.test(clean.tenantId)) {
          toast.error('Tenant inválido');
          return;
        }
        const r = await updateUser(currentItem._id, clean);
        if (r?.success) {
          toast.success('Usuario actualizado');
        } else {
          toast.error('Error al actualizar', r?.message);
          return;
        }
      }
      handleCloseModal();
      fetchAndSetUsers();
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    }
  };

  const handleDeleteConfirmation = (item) => {
    setItemToDelete(item);
    setIsDeleteDialogOpen(true);
  };

  const handleDeleteItem = async () => {
    if (!itemToDelete) return;
    try {
      const r = await deleteUser(itemToDelete._id);
      if (r?.success) {
        toast.success('Usuario eliminado', itemToDelete.nombre);
      } else {
        toast.error('Error al eliminar', r?.message);
        return;
      }
    } catch (e) {
      toast.error('Error inesperado', e?.message);
    } finally {
      setIsDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchAndSetUsers();
    }
  };

  const handleTableAction = (actionType, row) => {
    if (actionType === 'edit') return handleOpenModal('edit', row);
    if (actionType === 'delete') return handleDeleteConfirmation(row);
  };

  const handleApplyFilters = (applied) => {
    setFilters(applied);
    setCurrentPage(1);
  };

  const handleRemoveFilter = (key) => {
    setFilters((prev) => {
      const updated = { ...prev };
      delete updated[key];
      return updated;
    });
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPage(1);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      transition={{ duration: 0.3 }}
      className="space-y-4"
    >
      <Card>
        <CardHeader className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="bg-primary/10 p-3 rounded-lg">
              <Users className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <CardTitle className="text-2xl">Gestión de Usuarios</CardTitle>
                {tenant ? (
                  <Badge variant="secondary" className="flex items-center gap-1 font-normal">
                    <Building2 className="h-3 w-3" />
                    {tenant.nombre || tenant.slug}
                  </Badge>
                ) : (
                  <Badge
                    variant="outline"
                    className="flex items-center gap-1 font-normal text-amber-600 border-amber-300"
                  >
                    <ShieldAlert className="h-3 w-3" />
                    Modo plataforma
                  </Badge>
                )}
              </div>
              <CardDescription>
                {tenant
                  ? `Usuarios del operador ${tenant.nombre || tenant.slug}.`
                  : 'Todos los usuarios de la plataforma y de los operadores.'}
              </CardDescription>
            </div>
          </div>
          <div className="flex flex-wrap gap-2 justify-end">
            <ExportExcelButton
              service={exportUsers}
              columns={columnsExcel}
              fileName={`usuarios_${new Date().toISOString().slice(0, 10)}`}
              filters={filters}
            />
            <Button variant="outline" onClick={() => setIsDrawerOpen(true)}>
              <FilterIcon className="mr-2 h-4 w-4" /> Filtros
            </Button>
            <Button onClick={() => handleOpenModal('create')}>
              <PlusCircle className="mr-2 h-4 w-4" /> Nuevo Usuario
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <AppliedFilters
            filters={filters}
            fields={filterFields}
            onRemoveFilter={handleRemoveFilter}
            onClear={handleClearFilters}
          />

          <DataTable
            columns={columns}
            data={displayedData}
            isLoading={isLoading}
            onAction={handleTableAction}
            emptyMessage="Sin usuarios"
            emptyHint="No hay usuarios que coincidan con los filtros aplicados. Crea uno nuevo o ajusta los filtros."
          />

          {!isLoading && totalItems > 0 && (
            <Pagination
              currentPage={currentPage}
              totalItems={totalItems}
              itemsPerPage={itemsPerPage}
              onPageChange={handlePageChange}
              onLimitChange={handleLimitChange}
            />
          )}
        </CardContent>
      </Card>

      <FilterDrawer
        isOpen={isDrawerOpen}
        onClose={() => setIsDrawerOpen(false)}
        fields={filterFields}
        initialFilters={filters}
        onApply={handleApplyFilters}
      />

      <DynamicFormModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSave={handleSaveItem}
        fields={formFields}
        item={currentItem}
        title={modalMode === 'create' ? 'Crear Usuario' : 'Editar Usuario'}
        maxWidth="max-w-xl"
      />

      <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar usuario?</AlertDialogTitle>
            <AlertDialogDescription>
              {itemToDelete && (
                <>
                  Se eliminará a <strong>{itemToDelete.nombre}</strong> ({itemToDelete.correo}).
                  Esta acción no se puede deshacer.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteItem}>Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </motion.div>
  );
}
